import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { cn } from '@core/lib/cn';

import { Spinner } from './Spinner';
import { buttonClasses, type ButtonSize, type ButtonVariant } from './styles';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and sets aria-busy, without changing the button's width. */
  loading?: boolean;
  /** Replaces the label while loading. Keep it a verb: "Placing order…". */
  loadingText?: string;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  className?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingText,
    fullWidth = false,
    leadingIcon,
    disabled,
    type = 'button',
    children,
    className,
    ...rest
  },
  ref,
) {
  // `aria-disabled` alongside `disabled` so assistive tech reports the control as
  // present-but-unavailable while it is busy, rather than skipping it silently.
  const inert = disabled === true || loading;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={inert}
      aria-disabled={inert || undefined}
      aria-busy={loading || undefined}
      className={buttonClasses(variant, size, fullWidth, className)}
    >
      {loading ? <Spinner size="sm" /> : leadingIcon}
      <span>{loading && loadingText ? loadingText : children}</span>
    </button>
  );
});

export interface ButtonLinkProps extends Omit<LinkProps, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  className?: string;
  /**
   * Renders as an inert element instead of a link. Links have no disabled state,
   * so this drops the href entirely — an `aria-disabled` anchor is still clickable.
   */
  disabled?: boolean;
}

/**
 * A navigation action that looks like a button.
 *
 * It is a real `<Link>` (and therefore a real `<a href>`), not a button with an
 * onClick that calls navigate(). That keeps long-press, middle-click,
 * open-in-new-tab and "copy link" working — all of which a customer on a phone
 * genuinely uses — and it works before hydration finishes.
 *
 * This is the one primitive that imports from react-router. That coupling is
 * deliberate: the alternative is every screen hand-rolling button styling onto a
 * Link, which is exactly how a 44px target gets lost.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    leadingIcon,
    className,
    disabled = false,
    children,
    ...rest
  },
  ref,
) {
  if (disabled) {
    return (
      <span
        aria-disabled="true"
        role="link"
        className={buttonClasses(variant, size, fullWidth, cn('opacity-60', className))}
      >
        {leadingIcon}
        <span>{children}</span>
      </span>
    );
  }

  return (
    <Link {...rest} ref={ref} className={buttonClasses(variant, size, fullWidth, className)}>
      {leadingIcon}
      <span>{children}</span>
    </Link>
  );
});

export interface ExternalButtonLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'href'> {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
}

/**
 * A button-shaped link to somewhere outside the app.
 *
 * Separate from ButtonLink because that one is a react-router `<Link>`, which
 * would try to route an absolute URL through the client router and 404. This is
 * a plain anchor, and it pins `rel="noopener noreferrer"` so a `target="_blank"`
 * added later cannot hand `window.opener` to another origin — the kind of thing
 * that is only ever forgotten at the call site.
 */
export const ExternalButtonLink = forwardRef<HTMLAnchorElement, ExternalButtonLinkProps>(
  function ExternalButtonLink(
    { href, variant = 'primary', size = 'md', fullWidth = false, className, children, ...rest },
    ref,
  ) {
    return (
      <a
        {...rest}
        ref={ref}
        href={href}
        rel="noopener noreferrer"
        className={buttonClasses(variant, size, fullWidth, className)}
      >
        <span>{children}</span>
      </a>
    );
  },
);
